import React, { useEffect, useState } from "react";
// Chakra imports
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Icon,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  Spinner,
  useColorModeValue,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { RiEyeCloseLine } from "react-icons/ri";
import { onAuthStateChanged } from "firebase/auth";
import { MdOutlineRemoveRedEye, MdOutlineMail } from "react-icons/md";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import DefaultAuth from "layouts/auth/Default";
import { auth } from "../../../firebase";
import useAuthentication from "hooks/UseAuthentication";
import LOGO from "../../../assets/img/dark-mm-logo.svg";
import Analytics1 from "../../../assets/img/Analytics1.svg";
import Analytics2 from "../../../assets/img/Analytics2.svg";
import Analytics3 from "../../../assets/img/Analytics3.svg";
import Analytics4 from "../../../assets/img/Analytics4.svg";

const SignIn = () => {
  const textColor = "white";
  const accentColor = "#23A979";
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuthentication();
  const [show, setShow] = useState(false);
  const navigate = useNavigate();

  const schema = yup.object().shape({
    Email: yup.string()
      .email("Invalid Email Format")
      .required("Email is Required"),
    Password: yup.string().required("Password is required"),
  });

  const {
    handleSubmit,
    formState: { errors },
    register,
    getValues,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
  });

  const onSubmit = async () => {
    setIsLoading(true);
    const formValues = getValues();
    await login(formValues.Email, formValues.Password);
    setIsLoading(false);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        navigate("/admin/dashboard");
      }
    });
    return () => unsubscribe(); // Cleanup the listener when the component unmounts
  }, [navigate]);

  return (
    <DefaultAuth>
      {/* Centered glassmorphic card for login form */}
      <Flex
        direction="column"
        align="center"
        justify="center"
        bg="rgba(21, 28, 36, 0.75)"
        borderRadius="2xl"
        boxShadow="0 8px 32px 0 rgba(31, 38, 135, 0.18)"
        backdropFilter="blur(18px)"
        border="1.5px solid rgba(35, 169, 121, 0.15)"
        w={["90%", "85%", "80%", "80%"]}
        maxW="450px"
        p={8}
        position="absolute"
        left={{ base: "50%", md: "25%" }}
        transform="translateX(-50%)"
        zIndex={1}
        _before={{
          content: '""',
          position: "absolute",
          top: "-2px",
          left: "-2px",
          right: "-2px",
          bottom: "-2px",
          borderRadius: "2xl",
          border: "2px solid #23A979",
          opacity: 0.1,
          zIndex: -1,
          pointerEvents: "none",
        }}
      >
        <Flex direction="column" align="center" justify="center" w="100%">
          <Image 
            src={LOGO} 
            alt="MyMotor Logo" 
            width="auto"
            height="44px"
            mb={6} 
            filter="drop-shadow(0 2px 8px rgba(0, 0, 0, 0.4))" 
            objectFit="contain"
            preserveAspectRatio="xMidYMid meet"
          />
          <Heading color={textColor} fontSize="2xl" mb={2} fontWeight="bold" letterSpacing={1.2}>
            Welcome to Zoop CMS
          </Heading>
          <Text color="brand.500" fontSize="lg" mb={1} fontWeight="semibold" letterSpacing={0.6}>
            Powering the Future of Charging
          </Text>
          <Text color="gray.300" fontSize="md" mb={8}>
            Please sign in to your account
          </Text>
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
            <FormControl isInvalid={errors.Email} mb={6}>
              <FormLabel color={textColor} fontWeight="semibold">
                Email
              </FormLabel>
              <InputGroup>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  {...register("Email")}
                  bg="rgba(255,255,255,0.13)"
                  border="1.5px solid rgba(35, 169, 121, 0.22)"
                  color={textColor}
                  fontSize="lg"
                  py={6}
                  px={5}
                  borderRadius="xl"
                  _focus={{
                    borderColor: accentColor,
                    boxShadow: `0 0 0 2px ${accentColor}`,
                    bg: "rgba(255,255,255,0.22)",
                  }}
                  transition="all 0.18s cubic-bezier(.4,2,.6,1)"
                />
                <InputRightElement pointerEvents="none">
                  <Icon as={MdOutlineMail} color={accentColor} boxSize={6} />
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>{errors.Email && errors.Email.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={errors.Password} mb={2}>
              <FormLabel color={textColor} fontWeight="semibold">
                Password
              </FormLabel>
              <InputGroup>
                <Input
                  type={show ? "text" : "password"}
                  placeholder="Enter your password"
                  {...register("Password")}
                  bg="rgba(255,255,255,0.13)"
                  border="1.5px solid rgba(35, 169, 121, 0.22)"
                  color={textColor}
                  fontSize="lg"
                  py={6}
                  px={5}
                  borderRadius="xl"
                  _focus={{
                    borderColor: accentColor,
                    boxShadow: `0 0 0 2px ${accentColor}`,
                    bg: "rgba(255,255,255,0.22)",
                  }}
                  transition="all 0.18s cubic-bezier(.4,2,.6,1)"
                />
                <InputRightElement width="3rem">
                  <Button
                    variant="ghost"
                    _hover={{ bg: "transparent" }}
                    _active={{ bg: "transparent" }}
                    onClick={() => setShow(!show)}
                    tabIndex={-1}
                  >
                    {show ? (
                      <Icon as={RiEyeCloseLine} color={accentColor} boxSize={6} />
                    ) : (
                      <Icon as={MdOutlineRemoveRedEye} color={accentColor} boxSize={6} />
                    )}
                  </Button>
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>{errors.Password && errors.Password.message}</FormErrorMessage>
            </FormControl>

            <Flex justify="space-between" align="center" mt={2} mb={8} w="100%">
              <Box />
              <Text
                as="button"
                color={accentColor}
                fontSize="sm"
                fontWeight="medium"
                bg="none"
                border="none"
                cursor="pointer"
                _hover={{ textDecoration: "underline", color: "brand.600" }}
                onClick={() => navigate("/auth/forgotpassword")}
                transition="color 0.15s"
                type="button"
              >
                Forgot password?
              </Text>
            </Flex>

            <Button
              type="submit"
              w="100%"
              py={6}
              fontSize="lg"
              fontWeight="bold"
              color="white"
              bg="brand.500"
              boxShadow="0 2px 16px 0 rgba(35, 169, 121, 0.18)"
              borderRadius="xl"
              _hover={{
                bg: accentColor,
                boxShadow: "0 4px 24px 0 rgba(35, 169, 121, 0.29)",
                transform: "scale(1.03)",
              }}
              _active={{
                bg: accentColor,
                boxShadow: "0 2px 12px 0 rgba(35, 169, 121, 0.16)",
              }}
              transition="all 0.18s cubic-bezier(.4,2,.6,1)"
              isLoading={isLoading}
              loadingText="Signing in..."
            >
              Sign In
            </Button>
          </form>
        </Flex>
      </Flex>

      {/* Right side carousel */}
      <Box
        position="absolute"
        top="0"
        right="0"
        width="50%"
        height="100%"
        zIndex={0}
        display={["none", "none", "flex"]}
        alignItems="center"
        justifyContent="center"
        bg="white"
      >
        {/* White carousel container */}
        <Box
          width="95%"
          maxW="750px"
          height="auto"
          bg="white"
          borderRadius="2xl"
          boxShadow="0 8px 32px 0 rgba(31, 38, 135, 0.08)"
          border="1.5px solid rgba(35, 169, 121, 0.15)"
          p={8}
          position="relative"
          overflow="hidden"
          _before={{
            content: '""',
            position: "absolute",
            top: "-2px",
            left: "-2px",
            right: "-2px",
            bottom: "-2px",
            borderRadius: "2xl",
            border: "2px solid #23A979",
            opacity: 0.1,
            zIndex: -1,
            pointerEvents: "none",
          }}
        >
          {/* Carousel using react-slick with custom dot styling */}
          <Slider
            dots={true}
            infinite={true}
            speed={500}
            slidesToShow={1}
            slidesToScroll={1}
            autoplay={true}
            autoplaySpeed={4000}
            pauseOnHover={true}
            arrows={false}
            cssEase="cubic-bezier(0.4, 0, 0.2, 1)"
            dotsClass="slick-dots custom-dots"
            customPaging={(i) => (
              <Box
                as="button"
                width="10px"
                height="10px"
                borderRadius="full"
                bg="rgba(35, 169, 121, 0.4)"
                transition="all 0.3s ease"
                sx={{
                  '&.slick-active': {
                    bg: "#23A979 !important",
                    transform: "scale(1.2)",
                    boxShadow: "0 0 8px rgba(35, 169, 121, 0.5)",
                  }
                }}
                _hover={{
                  bg: "rgba(35, 169, 121, 0.7)",
                  transform: "scale(1.2)",
                }}
              />
            )}
          >
            {/* Slide 1: Command Center */}
            <Box textAlign="center" px={6} py={8}>
              <Image
                src={Analytics1}
                alt="Centralized Command Center"
                mx="auto"
                height="280px"
                width="auto"
                objectFit="contain"
                mb={5}
                filter="drop-shadow(0 8px 20px rgba(35, 169, 121, 0.2))"
              />
              <Heading color="#2D3748" fontSize="2xl" mb={3} fontWeight="600">
                Centralized Command Center
              </Heading>
              <Text color="rgba(45, 55, 72, 0.8)" fontSize="lg">
                Power at Your FingerTips - Total Control Over Your Charging
                Stations
              </Text>
            </Box>

            {/* Slide 2: Advanced Tech */}
            <Box textAlign="center" px={6} py={8}>
              <Image
                src={Analytics2}
                alt="Advanced Technological Integration"
                mx="auto"
                height="280px"
                width="auto"
                objectFit="contain"
                mb={5}
                filter="drop-shadow(0 8px 20px rgba(35, 169, 121, 0.2))"
              />
              <Heading color="#2D3748" fontSize="2xl" mb={3} fontWeight="600">
                Advanced Technological Integration
              </Heading>
              <Text color="rgba(45, 55, 72, 0.8)" fontSize="lg">
                Future-Ready Charging: Advanced Tech for Tomorrow's Vehicles
              </Text>
            </Box>

            {/* Slide 3: Analytics */}
            <Box textAlign="center" px={6} py={8}>
              <Image
                src={Analytics3}
                alt="Performance Analytics"
                mx="auto"
                height="280px"
                width="auto"
                objectFit="contain"
                mb={5}
                filter="drop-shadow(0 8px 20px rgba(35, 169, 121, 0.2))"
              />
              <Heading color="#2D3748" fontSize="2xl" mb={3} fontWeight="600">
                Performance Analytics
              </Heading>
              <Text color="rgba(45, 55, 72, 0.8)" fontSize="lg">
                Insightful Analytics: Performance Tracking Made Easy
              </Text>
            </Box>

            {/* Slide 4: Notifications */}
            <Box textAlign="center" px={6} py={8}>
              <Image
                src={Analytics4}
                alt="Real-Time Notifications"
                mx="auto"
                height="280px"
                width="auto"
                objectFit="contain"
                mb={5}
                filter="drop-shadow(0 8px 20px rgba(35, 169, 121, 0.2))"
              />
              <Heading color="#2D3748" fontSize="2xl" mb={3} fontWeight="600">
                Real-Time Notifications
              </Heading>
              <Text color="rgba(45, 55, 72, 0.8)" fontSize="lg">
                Stay Connected, Stay Informed with Real-Time Alerts
              </Text>
            </Box>
          </Slider>

          {/* Subtle glow effect */}
          <Box
            position="absolute"
            bottom="-50px"
            left="50%"
            transform="translateX(-50%)"
            width="80%"
            height="100px"
            bgGradient="radial(circle at center, rgba(35, 169, 121, 0.2), transparent 70%)"
            filter="blur(20px)"
            zIndex="-1"
          />
        </Box>
      </Box>
    </DefaultAuth>
  );
};

export default SignIn;
